import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, message } from 'antd';
import { StyledButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { EmailFigCaptionWrap, EmailWrap, FigCaption, LicenseInputWrap, LicenseKeyWrap } from 'pages/license/style';
import { ContentWrap, SubHeader } from '../ad_domain/style';
import { StyledFormItem } from '../ad_group/style';
import { useDispatch } from 'react-redux';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { getLicenseTier, resetProgress, setCurrent, setStatus, setupActivateLicense } from 'redux/slices/setupSlice';
import { InsideContent, SubHeaderWrap } from './style';

// Helper: extract a readable string from any API error response
const extractErrorMessage = (data) => {
  if (!data) return 'Something went wrong. Please try again.';
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    // Show field-level validation errors (e.g. { errors: { LicenseKey: ["The LicenseKey field is required."] } })
    if (data.errors) {
      const messages = Object.entries(data.errors)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
        .join('\n');
      if (messages) return messages;
    }
    if (data.detail) return data.detail;
    if (data.title) return data.title;
    if (data.message) return data.message;
    return JSON.stringify(data);
  }
  return 'Something went wrong. Please try again.';
};

const LicenseConfig = () => {
  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [licenseParts, setLicenseParts] = useState(['', '', '', '']);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(!licenseParts.every((part) => part.length === 4));

  useEffect(() => {
    dispatch(setStatus('process'));
    dispatch(resetProgress());
  }, []);

  useEffect(() => {
    setIsSubmitDisabled(!licenseParts.every((part) => part.length === 4));
  }, [licenseParts]);

  const handleInputChange = (index, value) => {
    value = value.toUpperCase();
    if (value.length <= 4) {
      const newParts = [...licenseParts];
      newParts[index] = value;
      setLicenseParts(newParts);
      if (value.length === 4 && index < 3) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const _licenseParts = [...licenseParts];
    const pasteData = e.clipboardData.getData('text/plain').trim().toUpperCase();
    if (pasteData) {
      const breakPoints = /\-|\s|\$|\_/;
      const splitValue = pasteData.split(breakPoints, 4).join('');
      const modifiedValue = splitValue.match(/(.{1,4})/g);

      modifiedValue.forEach((ele) => {
        if (index < _licenseParts.length) {
          _licenseParts[index] = ele;
          if (_licenseParts[index].length === 4 && index < 3) {
            inputRefs.current[index + 1].focus();
          } else if (index < 3) {
            inputRefs.current[index].focus();
          }
          index += 1;
        }
      });

      setLicenseParts(_licenseParts);
    }
  };

  const checkCondition = async (value) => {
    try {
      const response = await dispatch(getLicenseTier(value));
      return response.payload?.data?.tierInfo ?? null;
    } catch (error) {
      return null;
    }
  };

  const handleSubmit = async (formValues) => {
    // Build payload with correct casing to match C# AddLicense model
    const payload = {
      Email: formValues.email,
      LicenseKey: licenseParts.join('-'),
      productCode: 'adweb',
      uid: '',
      TierInfo: null,
      Domains: null,
    };

    try {
      setLoading(true);
      // Get tier info first
      const tierResult = await checkCondition({
        email: payload.Email,
        licenseKey: payload.LicenseKey,
        productCode: payload.productCode,
      });
      const tierNum = parseInt(tierResult, 10);
      payload.TierInfo = isNaN(tierNum) ? null : tierNum;

      const response = await dispatch(setupActivateLicense(payload));

      if (response.payload && response.payload.status === 200) {
        dispatch(setStatus('finish'));
        await message.success(
          typeof response.payload.data === 'string' ? response.payload.data : 'License activated successfully!'
        );
        dispatch(setCurrent(3));
      } else {
        const errorMsg = extractErrorMessage(response.payload?.data);
        SweetSweetAlert({ sweetData: errorMsg, icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ sweetData: 'Something went wrong. Please try again.', icon: 'error', buttonColor: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentWrap className='centerContent'>
      <InsideContent>
        <SubHeaderWrap>
          <SubHeader>Activation Details:</SubHeader>
        </SubHeaderWrap>
        <div>
          <Form layout='vertical' onFinish={handleSubmit} initialValues={{ email: '', licenseKey: '' }}>
            <EmailWrap>
              <Form.Item name='email' label='Email ID' style={{ marginBottom: 0 }}>
                <Input />
              </Form.Item>
              <EmailFigCaptionWrap>
                <span style={{ color: 'red' }}>*</span>
                <FigCaption>The email id you used to purchase this product.</FigCaption>
              </EmailFigCaptionWrap>
            </EmailWrap>
            <LicenseKeyWrap>
              <Form.Item name='licenseKey' label='Product Key'>
                <LicenseInputWrap>
                  {licenseParts.map((part, index) => (
                    <React.Fragment key={index}>
                      <Input
                        ref={(el) => (inputRefs.current[index] = el)}
                        value={part}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onPaste={(e) => handlePaste(e, index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && part === '' && index > 0) {
                            inputRefs.current[index - 1].focus();
                          }
                        }}
                        style={{ width: '78px', marginRight: '5px', height: '32px' }}
                      />
                      {index < 3 && <span style={{ marginRight: '5px' }}>-</span>}
                    </React.Fragment>
                  ))}
                </LicenseInputWrap>
              </Form.Item>
            </LicenseKeyWrap>
            <StyledFormItem>
              <StyledButton type='primary' htmlType='submit' disabled={isSubmitDisabled || loading}>
                Activate
              </StyledButton>
            </StyledFormItem>
          </Form>
        </div>
      </InsideContent>
    </ContentWrap>
  );
};
export default LicenseConfig;
